import { Client } from "@genesys/gc-mfe-auth";
import getGcuiAuthToken from "util/getGcuiAuthToken";
import { appName } from "util/genesysAppHeader";
import LOG from "services/loggingService";
import Bluebird from "bluebird";

/**
 * Auth client service using @genesys/gc-mfe-auth
 * This service provides a centralized way to manage authentication tokens.
 *
 * For localhost development:
 * - Reads tokens from localStorage (gcui_auth-localhost-dca/tca/pca)
 * - Automatically syncs to sessionStorage (gcui_auth)
 * - This allows tokens to persist across tabs while maintaining compatibility
 *
 * For production:
 * - Uses @genesys/gc-mfe-auth Client for token management
 */
class AuthClientService {
    private isLocalhost: boolean;
    private authClient: Client | null;
    private cleanupTokenChangeListener: (() => void) | null;

    constructor() {
        this.isLocalhost = this.detectLocalhost();
        this.authClient = null;
        this.cleanupTokenChangeListener = null;

        if (this.isLocalhost) {
            LOG.debug(
                "Running in localhost mode - using gcui_auth from localStorage, syncing to sessionStorage"
            );
        } else {
            try {
                // Initialize the auth client with the app name from genesysAppHeader
                this.authClient = new Client(appName);

                // Register token change handler for future-proofing
                this.cleanupTokenChangeListener = this.authClient.onTokenChange(
                    (newToken: string) => {
                        // TODO: Implement support for token rotation scenario
                        // Note: Currently, there is no defined use case for updating the token.
                        LOG.debug("Auth token updated successfully");
                    }
                );
            } catch (error) {
                LOG.debug("Failed to initialize auth client:", error);
                this.authClient = null;
            }
        }
    }

    /**
     * Detect if we're running in localhost development mode
     */
    private detectLocalhost(): boolean {
        return (
            window.location.hostname === "localhost" ||
            window.location.hostname === "127.0.0.1"
        );
    }

    /**
     * Get the current authentication token
     *
     * For localhost:
     * - Retrieves token from localStorage (via getGcuiAuthToken)
     * - Automatically syncs to sessionStorage['gcui_auth']
     *
     * For production:
     * - Uses @genesys/gc-mfe-auth Client to get the token
     */
    getToken(): Promise<string | null> {
        if (this.isLocalhost) {
            const token = this.getTokenFromLocalStorage();

            // Sync to sessionStorage
            const currentSessionToken =
                window.sessionStorage.getItem("gcui_auth");
            if (token !== currentSessionToken) {
                if (token) {
                    window.sessionStorage.setItem("gcui_auth", token);
                } else if (currentSessionToken) {
                    // clean up stale sessionStorage token when localStorage is cleared
                    window.sessionStorage.removeItem("gcui_auth");
                }
            }

            return Bluebird.resolve(token);
        }

        if (!this.authClient) {
            LOG.warn(
                "Auth client not available - initialization may have failed"
            );
            return Bluebird.resolve(null);
        }

        return Bluebird.resolve(this.authClient.getToken()).catch(
            (error: Error) => {
                LOG.warn("Failed to get auth token:", error);
                return null;
            }
        );
    }

    /**
     * Get token from localStorage (for localhost development)
     *
     * Retrieves the token from localStorage using environment-specific keys:
     * - gcui_auth-localhost-dca (default)
     * - gcui_auth-localhost-tca (test environment)
     * - gcui_auth-localhost-pca (production environment)
     *
     * The token is stored in localStorage so it persists across tabs.
     * The getToken() method handles syncing it to sessionStorage for pc-ui-auth compatibility.
     *
     * @returns {string | null} The access token string, or null if not found
     */
    private getTokenFromLocalStorage(): string | null {
        try {
            return getGcuiAuthToken();
        } catch (error) {
            LOG.debug("Failed to get auth token from localStorage:", error);
            return null;
        }
    }

    /**
     * Notify the host app about a 401 response due to token expiration
     */
    notify401Response(expiredToken: string | null): void {
        if (this.isLocalhost) {
            // For localhost, just log the 401 - no host app to notify
            LOG.info("401 response received in localhost mode:", expiredToken);
            return;
        }

        if (!this.authClient) {
            LOG.warn("Cannot notify 401 response - auth client not available");
            return;
        }

        if (!expiredToken) {
            LOG.error("Cannot notify 401 response - no token provided");
            return;
        }

        try {
            this.authClient.notify401Response(expiredToken);
        } catch (error) {
            LOG.warn("Failed to notify 401 response:", error);
        }
    }

    /**
     * Dispose of the auth client and clean up listeners
     * Only call this if explicitly needed for cleanup
     */
    dispose(): void {
        if (this.cleanupTokenChangeListener) {
            this.cleanupTokenChangeListener();
            this.cleanupTokenChangeListener = null;
        }
        this.authClient = null;
    }
}

const authClientService = new AuthClientService();

export default authClientService;
